import tokens from '#ynt-design-system/tokens.module.scss'
import type { BadgeType } from '#ynt-design-system/components/badge/badge.tsx'

type Lab = [number, number, number]

/** Below this OKLab chroma a colour reads as grey, and is matched to a grey fill. */
const GREY_CHROMA = 0.04

const FILLS: Record<BadgeType, string> = {
  secondary: tokens['badge-bg-secondary'],
  brand: tokens['badge-bg-brand'],
  informative: tokens['badge-bg-informative'],
  success: tokens['badge-bg-success'],
  warning: tokens['badge-bg-warning'],
  danger: tokens['badge-bg-danger'],
  teal: tokens['badge-bg-teal'],
  pink: tokens['badge-bg-pink'],
}

function parseOklch(value: string): Lab | null {
  const match = /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)(?:deg)?/i.exec(value.trim())
  if (!match) return null
  const lightness = parseFloat(match[1]) / (match[2] ? 100 : 1)
  const chromaValue = parseFloat(match[3])
  const angle = (parseFloat(match[4]) * Math.PI) / 180
  return [lightness, chromaValue * Math.cos(angle), chromaValue * Math.sin(angle)]
}

function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([\da-f]{3}|[\da-f]{6})(?:[\da-f]{2})?$/i.exec(hex.trim())
  if (!match) return null
  const digits = match[1].length === 3 ? [...match[1]].map((d) => d + d).join('') : match[1]
  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16) / 255) as [number, number, number]
}

/** sRGB to OKLab, where straight-line distance tracks how different two colours look. */
function toOklab([r, g, b]: [number, number, number]): Lab {
  const linear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const [lr, lg, lb] = [linear(r), linear(g), linear(b)]
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

const chroma = ([, a, b]: Lab) => Math.hypot(a, b)
const hue = ([, a, b]: Lab) => Math.atan2(b, a)

let fills: { grey: BadgeType; hued: (readonly [BadgeType, number])[] } | undefined

/** Parsed on first use, skipping any fill whose token did not resolve to an OKLCH colour. */
function parsedFills() {
  if (fills) return fills
  const labs = (Object.entries(FILLS) as [BadgeType, string | undefined][]).flatMap(([type, color]) => {
    const lab = color ? parseOklch(color) : null
    return lab ? [[type, lab] as const] : []
  })
  fills = {
    grey: labs.find(([, lab]) => chroma(lab) < GREY_CHROMA)?.[0] ?? 'secondary',
    hued: labs.filter(([, lab]) => chroma(lab) >= GREY_CHROMA).map(([type, lab]) => [type, hue(lab)] as const),
  }
  return fills
}

/**
 * The badge type whose fill is closest in hue to a free-form colour, such as one picked from a
 * colour wheel; lightness is ignored, so a pastel keeps its hue. Greys match the grey fill
 * (`secondary`), which missing and unparseable colours also get.
 */
export function closestBadgeType(color: string | null | undefined): BadgeType {
  const rgb = color ? parseHex(color) : null
  if (!rgb) return 'secondary'
  const lab = toOklab(rgb)
  const { grey, hued } = parsedFills()
  if (chroma(lab) < GREY_CHROMA) return grey
  const target = hue(lab)
  let best = grey
  let bestDistance = Infinity
  for (const [type, fillHue] of hued) {
    const turn = Math.abs(target - fillHue) % (2 * Math.PI)
    const distance = Math.min(turn, 2 * Math.PI - turn)
    if (distance < bestDistance) {
      best = type
      bestDistance = distance
    }
  }
  return best
}
